import MineDrilling from '@/pages/reports/driller_prod/MineDrilling'
import Report from '@/components/Report'
import { useFilledReport, useReports } from '@/hooks'
import { useTranslation } from 'react-i18next'
import Drawing, { useDrawing } from '@/components/forms/Drawing'
import { useEffect, useRef, useState } from 'preact/hooks'
import { makeDrillEquipment, makeGeneralInformation, makeMatrixData } from './utils'
import Btn from '@/components/forms/Btn'
import classNames from 'classnames'
import { DateTime } from 'luxon'
import { isQueryLoading } from '@/helpers'
import Loading from '@/components/Loading'
import { getEquipments, getProjectsWithTask, getSettings, getTasks } from '@/api/resources'
import { useQuery } from '@tanstack/react-query'
import { TaskSelect } from '@/components/forms/FuzzySelect'
import CollapsibleFieldset from '@/components/CollapsibleFieldset'
import { eq, groupBy } from 'lodash-es'
import Input from '@/components/forms/Input'
import DrillerProdContext from './DrillerProdContext'
import { DrillEquipment, GeneralInformation } from './types'
import DrillHeader from '@/components/forms/DrillHeader'
import { isNumeric, parseDate } from '@/utils'



const DrillerProdReport: FC<ReportProps> = p => {
	const { t } = useTranslation('driller')
	const [formOption, setFormOption] = useState<string | undefined>(undefined)
	const [formOptionConfirmed, setFormOptionConfirmed] = useState(false)
	const [reportId, setReportId] = useState<number | string | undefined>(undefined)
	const r = useFilledReport(reportId?.toString() ?? p.report_id)
	const [equipments, setEquipments] = useState<DrillEquipment>(makeDrillEquipment(r))
	const [generalInformation, setGeneralInformation] = useState<GeneralInformation | undefined>(makeGeneralInformation(r))
	const { status: reportsStatus, fetchStatus: reportsFetchStatus, data: reports } = useReports()
	const { data: settings } = useQuery({
		queryKey: ['settings'],
		queryFn: getSettings,
	})
	const { status: projectsStatus, fetchStatus: projectsFetchStatus, data: projects } = useQuery({
		queryKey: ['projects-tasks'],
		queryFn: getProjectsWithTask,
	})
	const { data: equipmentsList, isLoading, error } = useQuery({
		queryKey: ['equipments'],
		queryFn: getEquipments,
	})
	const { data: tasks, isLoading: tasksLoading, error: tasksError } = useQuery({
		queryKey: ['tasks'],
		queryFn: getTasks,
	})

	useEffect(() => {
		if (r && projects) {
			const project = projects.find(p => p.id == r.project_id)
			if (project) {
				const gi = makeGeneralInformation(project.tasks || [], r)
				setGeneralInformation(gi)
			}
		}
	}, [r, projects])

	useEffect(() => {
		if (r && equipmentsList) {
			const de = makeDrillEquipment(equipmentsList, r)
			setEquipments(de)
		}
	}, [r, equipmentsList])

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);


	const defaultCollapsed = reportId != undefined && !p.disabled
	const { setBlob: setSupervisorSignature, append: appendSupervisorSignature } = useDrawing(
		'form[supervisor_signature]',
		'supervisor_signature.png'
	)

	const { setBlob: setDrillerSignature, append: appendDrillerSignature } = useDrawing(
		'form[driller_signature]',
		'driller_signature.png'
	)

	const { setBlob: setClientSignature, append: appendClientignature } = useDrawing(
		'form[client_signature]',
		'client_signature.png'
	)

	const removeNulls = (fd: FormData) => {
		if (!fd.get('equipments[0][equipment_id]')) {
			fd.delete('equipments[0][id]')
			fd.delete('equipments[0][start_time]')
			fd.delete('equipments[0][end_time]')
			fd.delete('equipments[0][duration]')
			fd.delete('equipments[0][diameter]')
			fd.delete('equipments[0][status][slug]')
		}

		if (!fd.get('equipments[1][equipment_id]')) {
			fd.delete('equipments[1][equipment_id]')
			fd.delete('equipments[1][start_time]')
			fd.delete('equipments[1][end_time]')
			fd.delete('equipments[1][duration]')
			fd.delete('equipments[1][status][slug]')
		}

		if (!fd.get('equipments[2][equipment_id]')) {
			fd.delete('equipments[2][equipment_id]')
			fd.delete('equipments[2][start_time]')
			fd.delete('equipments[2][end_time]')
			fd.delete('equipments[2][duration]')
			fd.delete('equipments[2][status][slug]')
		}

		for (var pair of fd.entries()) {
			if ((!pair[1] || pair[1] == '') && !pair[0].includes('matrix')) {
				fd.delete(pair[0])
			}
		}
	}

	const submitArgs = {
		async process(fd: FormData) {
			removeNulls(fd)
			appendSupervisorSignature(fd)
			appendDrillerSignature(fd)
			appendClientignature(fd)
		}
	}

	const handleFormOptionConfirmation = (e: Event) => {
		setFormOptionConfirmed(true)
	}

	const handleTimeChange = (e: Event, i: number, field: string) => {
		const target = e.target as HTMLInputElement
		const value = parseFloat(target.value)
		if (generalInformation?.form.time_entries) {
			const newTimeEntries = generalInformation?.form.time_entries.map((entry, j) => {
				if (i == j) {
					return { ...entry, [field]: isNaN(value) ? 0 : value }
				}
				return entry
			})
			setGeneralInformation({ ...generalInformation, form: { ...generalInformation.form, time_entries: newTimeEntries } })
		}
	}

	if (!p.disabled && (!formOption || !formOptionConfirmed) && !p.report_id) {
		return (
			<>
				<input type="radio" name="formOption" id="continueForm" onClick={(e) => setFormOption('continue_form')} />
				<label htmlFor="continueForm">{t('continue_existing_form')}</label>
				<br />
				<br />
				<input type="radio" name="formOption" id="newForm" onClick={(e) => setFormOption('new_form')} />
				<label htmlFor="newForm">{t('create_new_form')}</label>
				<br />
				<br />
				<Btn class={classNames('w-25')} onClick={handleFormOptionConfirmation}>
					{t('forms:next')}
				</Btn>
			</>
		)
	}

	if ((isQueryLoading(reportsStatus, reportsFetchStatus) || isQueryLoading(projectsStatus, projectsFetchStatus)) && formOption === 'continue_form' && !p.report_id) {
		return (
			<Loading />
		)
	}

	if (formOption === 'continue_form' && !reportId && !p.report_id) {
		return (
			<ol class='grid grid-cols-2 gap-4'>
				{(reports && projects) && reports.filter(r => r.form_type === 'driller_prod')
					.filter(r => r?.form?.is_complete != '1')
					.sort((a, b) => (parseDate(b.date_in)?.toMillis() ?? 0) - (parseDate(a.date_in)?.toMillis() ?? 0))
					.map(r => (
						<li>
							<a
								onClick={(e) => setReportId(r.id)}
								class='block h-full w-full rounded-md bg-white px-4 py-4 shadow-sm'
							>
								<div class='mb-auto font-medium text-gray-800'>
									{t('forms:project') + ': ' + (projects?.find(t => t.id == r.project_id)?.name ?? '-')}
								</div>
								<div class='mt-2 text-sm text-gray-600'>
									{r.form?.drilling_block != undefined ? t('drilling_block') + ': ' + r.form.drilling_block : ''}
								</div>
								<div class='mt-2 text-right text-sm text-gray-600'>
									{parseDate(r.date_in)?.toISODate()}
								</div>
							</a>
						</li>
					))}
			</ol>
		)
	}

	useEffect(() => {
		if (!reportId) {
			setReportId(crypto.randomUUID());
		}
	}, [])

	const indexedTimeEntries = generalInformation?.form.time_entries?.map((entry, index) => ({ ...entry, index }));
	const drillNumber = equipmentsList?.find(e => e.id === equipments?.drill.equipment_id)?.name ?? ''
	const compressorNumber = equipmentsList?.find(e => e.id === equipments?.compressor.equipment_id)?.name ?? ''
	const truckNumber = equipmentsList?.find(e => e.id === equipments?.truck.equipment_id)?.name ?? ''
	const drillDuration = (equipments?.drill?.end_time ?? 0) - (equipments?.drill?.start_time ?? 0)
	const compressorDuration = (equipments?.compressor?.end_time ?? 0) - (equipments?.compressor.start_time ?? 0)
	const truckDuration = (equipments?.truck?.end_time ?? 0) - (equipments?.truck.start_time ?? 0)

	const hideTravelingTimeEntries = settings?.find(s => s.name === 'hide_traveling_time_in_time_entries')?.value == 1
	const taskEntryClass = hideTravelingTimeEntries ? 'span3' : 'span2'

	const timeEntries = generalInformation?.form.time_entries ?? []

	return (
		<DrillerProdContext.Provider value={{ equipments: equipments, setEquipments: setEquipments, generalInformation: generalInformation, setGeneralInformation: setGeneralInformation }}>
			<Report submitArgs={submitArgs} type='driller_prod' report={r} reportId={reportId} disabled={p.disabled === 'disabled'}>
				<input type='hidden' name='version' defaultValue='2' />
				<input type="hidden" name="_method" value={isNumeric(r?.id) ? 'PUT' : 'POST'} />
				<DrillHeader report={r} disabled={p.disabled === 'disabled'} />
				<MineDrilling value={r} name='form' defaultCollapsed={defaultCollapsed}></MineDrilling>
				<CollapsibleFieldset title={t('driller:time_entries')}>
					{timeEntries ? Object.entries(groupBy(indexedTimeEntries, 'BT')).map(([category, tasks], index) => (
						<fieldset key={index}>
							<legend>{category}</legend>
							{tasks.map((task, i) => (
								<>
									<input type="hidden" name={`form[time_entries][${task.index}][task_id]`} value={task.task_id} />
									<TaskSelect
										required
										name={`form[time_entries][${task.index}][myoctans_task_id]`}
										selectedId={task.myoctans_task_id as number}
										label={t('task')}
										readOnly={true}
										className={taskEntryClass}
									/>
									<Input
										label={t('hours')}
										className={taskEntryClass}
										name={`form[time_entries][${task.index}][work_order_time]`}
										value={task.work_order_time}
										onBlur={(e) => handleTimeChange(e, task.index, 'work_order_time')}
										type="number"
										decimal
									/>
									{
										!hideTravelingTimeEntries &&
										<Input
											label={t('traveling')}
											className={taskEntryClass}
											name={`form[time_entries][${task.index}][traveling_time]`}
											value={task.traveling_time}
											onBlur={(e) => handleTimeChange(e, task.index, 'traveling_time')}
											type="number"
											decimal
										/>
									}
								</>
							))}
						</fieldset>
					)) :
						<p class="text-center">{t('driller:select_a_project')}</p>
					}
				</CollapsibleFieldset>
				<Input
					className='span6'
					label={t('production_report_hours')}
					type={'text'}
					disabled
					value={timeEntries.reduce((acc, entry) => acc + (entry.work_order_time ?? 0), 0)}
				/>
				<Input
					className='span6'
					label={t('drill_hours') + ': ' + drillNumber}
					type={'text'}
					disabled
					value={drillDuration}
				/>
				<Input
					className='span6'
					label={t('compressor_hours') + ': ' + compressorNumber}
					type={'text'}
					disabled
					value={compressorDuration}
				/>
				<Input
					className='span6'
					label={t('truck_hours') + ': ' + truckNumber}
					type={'text'}
					disabled
					value={truckDuration}
				/>
				<Drawing label={t('supervisor_signature')} setBlob={setSupervisorSignature} />
				<Drawing label={t('driller_signature')} setBlob={setDrillerSignature} />
				<Drawing label={t('client_signature')} setBlob={setClientSignature} />
			</Report>
		</DrillerProdContext.Provider>
	)
}
export default DrillerProdReport
